import type { UseFormReturnType } from '@mantine/form';
import type { TFunction } from 'i18next';
import { z } from 'zod/v3';

import type { TextAreaProps } from '@highlight-cp/core-ui';
import { TextArea } from '@highlight-cp/core-ui';

function extractUuids(x: unknown): string[] {
  if (typeof x !== 'string') {
    return [];
  }

  // Strip invalid characters and split into UUID-width chunks
  const uuids = x.replace(/[^0-9a-z-]/gi, '').match(/.{1,36}/g) ?? [];
  return Array.from(new Set(uuids));
}

export const uuidSchema = (message: string) =>
  z.string().refine(
    (value) => {
      const uuids = extractUuids(value);
      return uuids.length > 0 && uuids.every((uuid) => z.string().uuid().safeParse(uuid).success);
    },
    { message },
  );

export function createSchema<Field extends string>(
  t: TFunction<'translation', undefined>,
  field: Field,
  label = 'UUID',
) {
  return z.object({
    [field]: uuidSchema(t(`Must contain at least one valid ${label}`)),
  });
}

const createTextArea = <Field extends string>(
  t: TFunction<'translation', undefined>,
  field: Field,
  label = 'UUID',
) => {
  const Component = <Values extends Record<Field, string>>(
    form: UseFormReturnType<Values>,
    extras?: Partial<TextAreaProps> & { 'data-testid'?: string },
  ) => (
    <TextArea
      label={t(`Please provide {{label}}s`, { label })}
      placeholder={t(`Copy and paste {{label}}[s] here`, { label })}
      minRows={10}
      {...form.getInputProps(field)}
      {...(extras ?? {})}
    />
  );
  Component.displayName = 'UuidPasteBin';
  return Component;
};

export function useUuidPasteBin<Field extends string>(
  t: TFunction<'translation', undefined>,
  field: Field,
  label = 'UUID',
) {
  return {
    schema: createSchema(t, field, label),
    TextArea: createTextArea(t, field, label),
    extractUuids(values: Record<Field, string>) {
      return extractUuids(values[field]);
    },
  };
}
